import { Card, CbhIcon, Text } from "@clipboard-health/ui-components";
import { CardActionArea, Stack, useTheme } from "@mui/material";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports
import { formatInTimeZone } from "date-fns-tz";

import { BookingCardContentWrapper } from "../../Shift/CardContentWrapper";
import { TimeSlot } from "../../Shift/Open/types";
import { ShiftCardPillsWrapper } from "../../Shift/PillsWrapper";
import { BookingCardSummaryWrapper } from "../../Shift/SummaryWrapper";
import { TimeSlotIndicator } from "../../Shift/TimeSlotIndicator";
import { type SupportShift } from "../types";
import { CancellationStatusPill } from "./CancellationStatusPill";

interface SupportShiftCardProps {
  shift: SupportShift;
  onSelect: (shiftId: string) => void;
}

/**
 * Determines the time slot (AM/PM/NOC) based on the shift start time in local timezone.
 */
function getTimeSlotFromStartTime(startAt: string, timezone: string): TimeSlot {
  const startDate = parseISO(startAt);
  // Get the hour in the shift's timezone
  const hourString = formatInTimeZone(startDate, timezone, "H");
  const hour = Number(hourString);

  // NOC: 7pm (19:00) to 3am (03:00)
  // AM: 3am (03:00) to 11am (11:00)
  // PM: 11am (11:00) to 7pm (19:00)
  if (hour >= 19 || hour < 3) {
    return TimeSlot.NOC;
  }

  if (hour >= 3 && hour < 11) {
    return TimeSlot.AM;
  }

  return TimeSlot.PM;
}

/**
 * Card component displaying shift information for the support flow.
 * Uses the same visual patterns as other shift cards in the app.
 */
export function SupportShiftCard(props: SupportShiftCardProps) {
  const { shift, onSelect } = props;
  const theme = useTheme();

  const startDate = parseISO(shift.startAt);
  const endDate = parseISO(shift.endAt);

  const formattedDay = formatInTimeZone(startDate, shift.timezone, "EEE, MMM d");
  const formattedStartTime = formatInTimeZone(startDate, shift.timezone, "h:mm a").toLowerCase();
  const formattedEndTime = formatInTimeZone(endDate, shift.timezone, "h:mm a").toLowerCase();
  const timeRange = `${formattedStartTime} - ${formattedEndTime}`;

  const timeSlot = getTimeSlotFromStartTime(shift.startAt, shift.timezone);

  return (
    <Card
      outlined
      variant="quaternary"
      sx={{
        marginBottom: 4,
      }}
    >
      <CardActionArea
        aria-label={`Select shift at ${shift.workplaceName} on ${formattedDay}`}
        onClick={() => {
          onSelect(shift.id);
        }}
      >
        <BookingCardContentWrapper>
          {shift.deleted && (
            <ShiftCardPillsWrapper>
              <CancellationStatusPill
                label={
                  shift.cancellationDetails?.isSentHome ? "Sent Home" : "Canceled by Workplace"
                }
              />
            </ShiftCardPillsWrapper>
          )}

          <BookingCardSummaryWrapper>
            <TimeSlotIndicator timeSlot={timeSlot} />

            <Stack spacing={1} sx={{ flex: 1 }}>
              <Text variant="body2">{formattedDay}</Text>
              <Text semibold variant="body1">
                {timeRange}
              </Text>
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                {shift.workplaceName}
              </Text>
            </Stack>
          </BookingCardSummaryWrapper>

          <CbhIcon
            type="chevron-right"
            sx={{ position: "absolute", right: theme.spacing(6), top: theme.spacing(6) }}
          />
        </BookingCardContentWrapper>
      </CardActionArea>
    </Card>
  );
}
